import { ExtendedResultBodySpecialFloodedEvaporatorAPI } from './specialFloodedEvaporatorService';
import { ExtendedResultBodySpecialCondenserAPI } from './specialCondenserService';
import { ExtendedResultBodyWKCHeatExchangerAPI } from './coaxialCondenserService';
import { ExtendedResultBodyWKEHeatExchangerAPI } from './coaxialEvaporatorService';
import { ExtendedResultBodyCalculationAPI } from './coldplateService';
import { ExtendedResultBodyFloodedEvaporatorCalculation } from './floodedEvaporatorService';
import { ExtendedResultBodyDryExpansionEvaporatorCalculation } from './dryExpansionEvaporatorService';
import { ExtendedResultBodyCondenserCalculation } from './condenserService';
import * as pako from 'pako';

export type ShareableType =
  | 'special_flooded_evaporator'
  | 'special_condenser'
  | 'coaxial_condenser'
  | 'coaxial_evaporator'
  | 'cold_plate'
  | 'flooded_evaporator_rating'
  | 'flooded_evaporator_selection'
  | 'special_flooded_evaporator_ai'
  | 'dry_expansion_evaporator_rating'
  | 'dry_expansion_evaporator_selection'
  | 'condenser_rating'
  | 'condenser_selection'
  | 'air_heat_exchanger_rating'
  | 'air_heat_exchanger_selection';

export type ShareableData = {
  app: string;
  type: ShareableType;
  version: string;
  fileName: string;
  data: ExtendedResultBodySpecialFloodedEvaporatorAPI | ExtendedResultBodySpecialCondenserAPI | ExtendedResultBodyWKCHeatExchangerAPI | ExtendedResultBodyWKEHeatExchangerAPI | ExtendedResultBodyCalculationAPI | ExtendedResultBodyFloodedEvaporatorCalculation | ExtendedResultBodyDryExpansionEvaporatorCalculation | ExtendedResultBodyCondenserCalculation;
};

// High-performance compression using pako (gzip)
const compress = (str: string): string => {
  try {
    // Convert string to Uint8Array
    const data = new TextEncoder().encode(str);
    
    // Compress using pako (gzip)
    const compressed = pako.gzip(data);
    
    // Convert to base64 and make URL safe
    const base64 = btoa(String.fromCharCode.apply(null, Array.from(compressed)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  } catch (error) {
    console.error('Compression error:', error);
    // Fallback to original string if compression fails
    return str;
  }
};

const decompress = (str: string): string => {
  try {
    // Restore base64 padding and convert back from URL-safe format
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    
    // Decode base64
    const binaryString = atob(base64);
    const data = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      data[i] = binaryString.charCodeAt(i);
    }
    
    // Decompress using pako
    const decompressed = pako.ungzip(data);
    
    // Convert back to string
    return new TextDecoder().decode(decompressed);
  } catch (error) {
    console.error('Decompression error:', error);
    // Fallback to original string if decompression fails
    return str;
  }
};

class SharingService {
  private readonly STORAGE_KEY = 'shared_data';

  private encodeData(data: ShareableData): string {
    try {
      // Convert data to JSON string
      const jsonString = JSON.stringify(data);
      
      // Apply high-performance compression
      return compress(jsonString);
    } catch (error) {
      console.error('Error encoding data:', error);
      throw new Error('Failed to encode data');
    }
  }

  public decodeData(encodedData: string): ShareableData | null {
    try {
      // First try the new pako compression
      let jsonString = decompress(encodedData);
      
      // If pako decompression fails, try the old format for backward compatibility
      if (!jsonString || jsonString === encodedData) {
        try {
          // Restore base64 padding and convert back from URL-safe format
          let base64 = encodedData.replace(/-/g, '+').replace(/_/g, '/');
          while (base64.length % 4) base64 += '=';
          
          // Decode base64 and handle Unicode
          const compressed = decodeURIComponent(escape(atob(base64)));
          
          // Try old decompression method
          jsonString = this.oldDecompress(compressed);
        } catch (oldError) {
          console.error('Both new and old decompression failed:', oldError);
          return null;
        }
      }
      
      const data = JSON.parse(jsonString);

      // Validate the data structure
      if (!data.app || !data.type || !data.version || !data.fileName || !data.data) {
        console.error('Invalid data structure:', data);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error decoding data:', error);
      return null;
    }
  }

  // Old decompression method for backward compatibility
  private oldDecompress(str: string): string {
    // Restore quotes around property names (old format)
    const restoredJson = str.replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*):/g, '$1"$2":');
    return restoredJson;
  }

  storeSharedData(data: ShareableData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error storing shared data:', error);
      throw new Error('Failed to store shared data');
    }
  }

  getSharedData(): ShareableData | null {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error retrieving shared data:', error);
      return null;
    }
  }

  clearSharedData(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing shared data:', error);
    }
  }

  public generateShareUrl(data: ShareableData): string {
    try {
      const encodedData = this.encodeData(data);
      
      // Determine the base URL and path based on the environment
      const isCloud = window.location.hostname.includes('azure-api.net');
      const baseUrl = isCloud 
        ? 'https://devapimgmtthermalxeuw01.azure-api.net/devthermalx02app'
        : window.location.origin;
      
      // Use the /share endpoint to handle the data parsing and navigation
      return `${baseUrl}/share?data=${encodedData}`;
    } catch (error) {
      console.error('Error generating share URL:', error);
      throw new Error('Failed to generate share URL');
    }
  }

  public parseUrlData(): ShareableData | null {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const encodedData = urlParams.get('data');
      
      if (!encodedData) {
        return null;
      }

      const decodedData = this.decodeData(encodedData);
      if (!decodedData) {
        return null;
      }

      return decodedData;
    } catch (error) {
      console.error('Error parsing URL data:', error);
      return null;
    }
  }
}

export const sharingService = new SharingService(); 
